import React, { useState, useEffect, useMemo } from 'react';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { IoClose as Close } from 'react-icons/io5';
import { MdEdit as EditIcon, MdDeleteOutline as DeleteIcon, MdOutlineWarningAmber as EscalationIcon, MdOutlineListAlt as PolicyIcon } from 'react-icons/md';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import PolicyRow from '@IssueConsoleForms/PolicyRow';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import EscalationLevelService from '@Helpers/URLHelpers/KaiziqResolve/EscalationLevelService';
import ProjectService from '@Helpers/URLHelpers/ProjectService';
import ErrorHandler from '@Root/ErrorHandler';
import './EscalationMatrixForm.scss';

const cn = 'escalationMatrixForm';

const PRIORITY_CONFIG = {
    critical: { label: 'Critical', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },
    high:     { label: 'High',     color: '#f97316', bg: 'rgba(249, 115, 22, 0.1)' },
    normal:   { label: 'Normal',   color: '#2784b9', bg: 'rgba(39, 132, 185, 0.1)' },
};

export default function EscalationMatrixForm(props) {

    const [cursor, setCursor] = useState('auto');
    const [userList, setUserList] = useState([]);
    const [isEdited, setIsEdited] = useState(false);
    const [selectedPriority, setSelectedPriority] = useState('critical');
    const [levels, setLevels] = useState([]);
    const [removedPolicyId, setRemovedPolicyId] = useState([]);
    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [snackBarType, setSnackBarType] = useState('success');
    const [snackBarMessage, setSnackBarMessage] = useState('');
    const [showAddLevelModal, setShowAddLevelModal] = useState(false);
    const [showEditLevelModal, setShowEditLevelModal] = useState(false);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [tempModalLevel, setTempModalLevel] = useState(null);
    const [levelToDelete, setLevelToDelete] = useState(null);
    const [levelTitle, setLevelTitle] = useState('');
    const { currentProjectId } = useReduxProject();
    const escalationLevelService = new EscalationLevelService();
    const projectService = new ProjectService();
    const errorHandler = new ErrorHandler();

    useEffect(() => {
        renderData();
    }, []);

    const renderData = async () => {
        await renderUserListData();
        setCursor('wait');
        try {
            let escalationLevelList = await escalationLevelService.getListByConnectionId(props?.kaiziqResolveConnectionId);
            if (escalationLevelList.status < 400) {
                escalationLevelList = JSON.parse(escalationLevelList.body);
                setLevels(escalationLevelList);
                setRemovedPolicyId([]);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    };

    const criticalLevels = useMemo(() => levels.filter(l => l.priority === 'critical'), [levels]);
    const highLevels    = useMemo(() => levels.filter(l => l.priority === 'high'),     [levels]);
    const normalLevels  = useMemo(() => levels.filter(l => l.priority === 'normal'),   [levels]);

    const activeLevels = useMemo(() => {
        if (selectedPriority === 'critical') return criticalLevels;
        if (selectedPriority === 'high')     return highLevels;
        return normalLevels;
    }, [selectedPriority, criticalLevels, highLevels, normalLevels]);

    const renderUserListData = async () => {
        let loadLoops = 0, loadingError = false, errorException;
        for (loadLoops = 0; loadLoops < 10; loadLoops++) {
            try {
                let userListObj = await projectService.getSimpleAssigneeUserListForProject(currentProjectId);
                userListObj = JSON.parse(userListObj.body);
                setUserList([...userListObj, { fullName: 'Unassigned', userName: 'unassigned', id: 0 }]);
                loadingError = false;
                break;
            } catch (err) {
                errorHandler.logError('User List', err);
                loadingError = true;
                errorException = err;
            }
        }
        if (loadLoops === 10 && loadingError) {
            errorHandler.handleError(errorException);
            return false;
        }
        return true;
    };

    const handleOpenAddLevelModal = () => {
        setLevelTitle('');
        setTempModalLevel({
            id: Date.now(),
            priority: selectedPriority,
            title: '',
            levelId: activeLevels.length + 1,
            kaiziqResolveConnectionId: props?.kaiziqResolveConnectionId,
            policyList: [],
            isNew: true,
        });
        setShowAddLevelModal(true);
    };

    const handleCloseAddLevelModal = () => {
        setShowAddLevelModal(false);
        setTempModalLevel(null);
        setLevelTitle('');
    };

    const handleOpenEditLevelModal = (level) => {
        setTempModalLevel({ ...level });
        setLevelTitle(level.title);
        setShowEditLevelModal(true);
    };

    const handleCloseEditLevelModal = () => {
        setShowEditLevelModal(false);
        setTempModalLevel(null);
        setLevelTitle('');
    };

    const handleOpenDeleteConfirmModal = (levelId) => {
        setLevelToDelete(levelId);
        setShowDeleteConfirmModal(true);
    };

    const handleCloseDeleteConfirmModal = () => {
        setLevelToDelete(null);
        setShowDeleteConfirmModal(false);
    };

    const handleConfirmDelete = async () => {
        setCursor('wait');
        try {
            const filterLevel = activeLevels.find(lvl => lvl.id === levelToDelete);
            if (filterLevel?.isNew) {
                setLevels(prev => prev.filter(lvl => lvl.id !== levelToDelete));
            } else {
                const response = await escalationLevelService.deleteEscalationLevel({ id: levelToDelete });
                if (response.status < 400) {
                    await renderData();
                }
            }
            setShowDeleteConfirmModal(false);
            setLevelToDelete(null);
            setSnackBarMessage('Level deleted successfully');
            setSnackBarType('success');
            setSnackBarOpen(true);
            setIsEdited(true);
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    };

    const persistEscalationLevels = async (updatedLevels, removedPolicyIds = removedPolicyId) => {
        setCursor('wait');
        try {
            const hasExistingLevels = updatedLevels.some(level => !level?.isNew);
            const bodyObj = hasExistingLevels
                ? { levels: updatedLevels, removedPolicyId: removedPolicyIds }
                : { levels: updatedLevels };

            const response = hasExistingLevels
                ? await escalationLevelService.updateEscalationLevel(bodyObj)
                : await escalationLevelService.createLevelAndPolicy(bodyObj);

            if (response.status < 400) {
                await renderData();
                setIsEdited(false);
                return true;
            }
            return false;
        } catch (err) {
            errorHandler.handleError(err);
            return false;
        } finally {
            setCursor('auto');
        }
    };

    const handleAddLevelSave = async () => {
        if (!levelTitle.trim()) return;
        if (!tempModalLevel?.policyList?.length) {
            setSnackBarMessage('Please add at least one policy before saving this level');
            setSnackBarType('error');
            setSnackBarOpen(true);
            return;
        }
        const newLevel = { ...tempModalLevel, title: levelTitle };
        const saveSuccess = await persistEscalationLevels([...levels, newLevel]);
        if (!saveSuccess) {
            setSnackBarMessage('Escalation matrix failed to update. Please try again.');
            setSnackBarType('error');
            setSnackBarOpen(true);
            return;
        }
        setShowAddLevelModal(false);
        setTempModalLevel(null);
        setLevelTitle('');
        setSnackBarMessage('Level added and saved successfully');
        setSnackBarType('success');
        setSnackBarOpen(true);
    };

    const handleEditLevelSave = async () => {
        if (!levelTitle.trim()) return;
        if (!tempModalLevel?.policyList?.length) {
            setSnackBarMessage('Please add at least one policy before updating this level');
            setSnackBarType('error');
            setSnackBarOpen(true);
            return;
        }
        const updatedLevel = { ...tempModalLevel, title: levelTitle };
        const updatedLevels = levels.map(level => level.id === updatedLevel.id ? updatedLevel : level);
        const saveSuccess = await persistEscalationLevels(updatedLevels);
        if (!saveSuccess) {
            setSnackBarMessage('Escalation matrix failed to update. Please try again.');
            setSnackBarType('error');
            setSnackBarOpen(true);
            return;
        }
        setShowEditLevelModal(false);
        setTempModalLevel(null);
        setLevelTitle('');
        setSnackBarMessage('Level updated and saved successfully');
        setSnackBarType('success');
        setSnackBarOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const saveSuccess = await persistEscalationLevels(levels, removedPolicyId);
        if (saveSuccess) {
            setSnackBarMessage('Escalation matrix saved');
            setSnackBarType('success');
            setSnackBarOpen(true);
            return;
        }
        setSnackBarMessage('Escalation matrix failed to save. Please try again later.');
        setSnackBarType('error');
        setSnackBarOpen(true);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        const saveSuccess = await persistEscalationLevels(levels, removedPolicyId);
        if (saveSuccess) {
            setSnackBarMessage('Escalation matrix updated');
            setSnackBarType('success');
            setSnackBarOpen(true);
            return;
        }
        setSnackBarMessage('Escalation matrix failed to update. Please try again later.');
        setSnackBarType('error');
        setSnackBarOpen(true);
    };

    const handleAddPolicy = (levelId, policiesObj) => {
        if (policiesObj.duration === 0) return;
        setIsEdited(true);
        if ((showAddLevelModal || showEditLevelModal) && tempModalLevel?.id === levelId) {
            setTempModalLevel(prev => ({ ...prev, policyList: [...(prev?.policyList || []), policiesObj] }));
            return;
        }
        setLevels(prev => prev.map(item =>
            item.id === levelId ? { ...item, policyList: [...item.policyList, policiesObj] } : item
        ));
    };

    const handleRemovePolicy = (levelId, policyId) => {
        if ((showAddLevelModal || showEditLevelModal) && tempModalLevel?.id === levelId) {
            setTempModalLevel(prev => ({
                ...prev,
                policyList: (prev?.policyList || []).filter(item => item.id !== policyId),
            }));
            if (!tempModalLevel?.isNew) {
                setRemovedPolicyId(prev => [...prev, policyId]);
            }
            setIsEdited(true);
            return;
        }
        setRemovedPolicyId(prev => [...prev, policyId]);
        setLevels(prev => prev.map(item =>
            item.id === levelId
                ? { ...item, policyList: item.policyList.filter(p => p.id !== policyId) }
                : item
        ));
        setIsEdited(true);
    };

    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackBarOpen(false);
    };

    const isSavingLevelModal = cursor === 'wait' && (showAddLevelModal || showEditLevelModal);
    const hasEmptyNewLevel = activeLevels.some(lvl => lvl.isNew && (!lvl.policyList || lvl.policyList.length === 0));

    const getSubmitButton = () => {
        if (activeLevels.length === 0) return null;
        const disabled = cursor === 'wait' || hasEmptyNewLevel;
        if (removedPolicyId?.length === 0 && levels?.length === 0) {
            return <BorderBlueButtonV2 text='Submit' disabled={disabled || activeLevels.length === 0} onClick={handleSubmit} />;
        }
        if (activeLevels.length === 1) {
            return <BorderBlueButtonV2 text='Update' disabled={disabled || activeLevels[0].policyList.length === 0} onClick={handleUpdate} />;
        }
        return <BorderBlueButtonV2 text='Save & Update' disabled={disabled} onClick={handleUpdate} />;
    };

    const renderLevelCard = (lvl, index) => (
        <div className={`${cn}__levelItem`} key={lvl.id}>
            <div className={`${cn}__levelItem__index`}>{index + 1}</div>
            <div className={`${cn}__levelItem__summary`}>
                <div className={`${cn}__levelItem__title`}>{lvl.title}</div>
                <div className={`${cn}__levelItem__meta`}>
                    <span className={`${cn}__levelItem__count`}>
                        {lvl.policyList?.length || 0} {lvl.policyList?.length === 1 ? 'policy' : 'policies'}
                    </span>
                </div>
            </div>
            <div className={`${cn}__levelItem__actions`}>
                <button
                    className={`${cn}__levelItem__btn ${cn}__levelItem__btn--edit`}
                    onClick={() => handleOpenEditLevelModal(lvl)}
                    title='Edit Level'>
                    <EditIcon />
                </button>
                <button
                    className={`${cn}__levelItem__btn ${cn}__levelItem__btn--delete`}
                    onClick={() => handleOpenDeleteConfirmModal(lvl.id)}
                    title='Delete Level'>
                    <DeleteIcon />
                </button>
            </div>
        </div>
    );

    const renderLevelPoliciesView = (level) => (
        <div className={`${cn}__modal__policies`}>
            <div className={`${cn}__modal__policies__header`}>
                <div className={`${cn}__modal__policies__header__col`}>Duration</div>
                <div className={`${cn}__modal__policies__header__col`}>If action not performed</div>
                <div className={`${cn}__modal__policies__header__col`}>Do this</div>
                <div className={`${cn}__modal__policies__header__col`}>New Assignee</div>
                <div className={`${cn}__modal__policies__header__col ${cn}__modal__policies__header__col--sm`}>Settings</div>
            </div>
            <PolicyRow
                levelId={level.id}
                userList={userList}
                policyList={level.policyList}
                priorityLevels={[level]}
                handleAddPolicy={handleAddPolicy}
                handleRemovePolicy={handleRemovePolicy} />
        </div>
    );

    const renderLevelModal = (isEdit) => {
        const open = isEdit ? showEditLevelModal : showAddLevelModal;
        const onClose = isEdit ? (isSavingLevelModal ? undefined : handleCloseEditLevelModal)
                                : (isSavingLevelModal ? undefined : handleCloseAddLevelModal);
        const onSave = isEdit ? handleEditLevelSave : handleAddLevelSave;
        const modalTitle = isEdit ? 'Edit Level' : 'Add New Level';
        const saveText = isSavingLevelModal ? 'Saving…' : (isEdit ? 'Update Level' : 'Add Level');

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth='lg'
                fullWidth
                className={`${cn}__modal__dialog`}>

                <DialogTitle className={`${cn}__modal__title`}>
                    <span className={`${cn}__modal__title__text`}>{modalTitle}</span>
                    <button
                        className={`${cn}__modal__title__close`}
                        onClick={isEdit ? handleCloseEditLevelModal : handleCloseAddLevelModal}
                        disabled={isSavingLevelModal}
                        aria-label='Close'>
                        <Close />
                    </button>
                </DialogTitle>

                <DialogContent className={`${cn}__modal__content`}>
                    <div className={`${cn}__modal__titleField`}>
                        <label>
                            Level Title <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>
                        </label>
                        <TextField
                            fullWidth
                            size='small'
                            type='text'
                            value={levelTitle}
                            onChange={(e) => setLevelTitle(e.target.value)}
                            placeholder='e.g., Level 1, Critical Response'
                        />
                    </div>

                    {tempModalLevel && (
                        <>
                            <div className={`${cn}__modal__sectionLabel`}>
                                <PolicyIcon />
                                Escalation Policies
                            </div>
                            {renderLevelPoliciesView(tempModalLevel)}
                        </>
                    )}
                </DialogContent>

                <DialogActions className={`${cn}__modal__actions`}>
                    <button
                        className={`${cn}__modal__cancelBtn`}
                        onClick={isEdit ? handleCloseEditLevelModal : handleCloseAddLevelModal}
                        disabled={isSavingLevelModal}>
                        Cancel
                    </button>
                    <BorderBlueButtonV2
                        text={saveText}
                        onClick={onSave}
                        disabled={isSavingLevelModal || !levelTitle.trim()}
                    />
                </DialogActions>
            </Dialog>
        );
    };

    return (
        <div className={cn} style={{ cursor }}>

            {/* ── Header ── */}
            <div className={`${cn}__header`}>
                <div className={`${cn}__header__left`}>
                    <div className={`${cn}__header__iconWrap`}>
                        <EscalationIcon />
                    </div>
                    <div className={`${cn}__header__text`}>
                        <p className={`${cn}__header__title`}>Escalation Matrix</p>
                        <p className={`${cn}__header__subtitle`}>Configure SLA escalation levels per priority</p>
                    </div>
                </div>
            </div>

            {/* ── Priority Switcher ── */}
            <div className={`${cn}__prioritySwitcher`}>
                <div className={`${cn}__prioritySwitcher__tabs`}>
                    {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                        <button
                            key={key}
                            className={`${cn}__prioritySwitcher__tab${selectedPriority === key ? ` ${cn}__prioritySwitcher__tab--active` : ''}`}
                            style={{ '--tab-color': cfg.color, '--tab-bg': cfg.bg }}
                            onClick={() => setSelectedPriority(key)}
                            disabled={isEdited && selectedPriority !== key}>
                            <span
                                className={`${cn}__prioritySwitcher__tab__dot`}
                                style={{ background: cfg.color }} />
                            {cfg.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Body ── */}
            <div className={`${cn}__body`}>
                {activeLevels.length === 0 ? (
                    <div className={`${cn}__empty`}>
                        <EscalationIcon className={`${cn}__empty__icon`} />
                        <p className={`${cn}__empty__text`}>No levels configured</p>
                        <p className={`${cn}__empty__hint`}>
                            Add a level to define escalation rules for{' '}
                            <strong>{PRIORITY_CONFIG[selectedPriority].label}</strong> priority issues.
                        </p>
                    </div>
                ) : (
                    <div className={`${cn}__levelsList`}>
                        {activeLevels.map((lvl, index) => renderLevelCard(lvl, index))}
                    </div>
                )}
                <button className={`${cn}__addBtn`} onClick={handleOpenAddLevelModal}>
                    + Add Level
                </button>
            </div>

            {/* ── Footer ── */}
            <div className={`${cn}__footer`}>
                {getSubmitButton()}
            </div>

            {/* ── Add / Edit Level Modals ── */}
            {renderLevelModal(false)}
            {renderLevelModal(true)}

            {/* ── Delete Confirmation ── */}
            <Dialog
                open={showDeleteConfirmModal}
                onClose={handleCloseDeleteConfirmModal}
                className={`${cn}__deleteDialog`}
                maxWidth='xs'
                fullWidth>
                <DialogTitle className={`${cn}__deleteDialog__title`}>
                    Delete Level
                </DialogTitle>
                <DialogContent className={`${cn}__deleteDialog__content`}>
                    <p className={`${cn}__deleteDialog__text`}>
                        Are you sure you want to delete this level? This action cannot be undone.
                    </p>
                </DialogContent>
                <DialogActions className={`${cn}__deleteDialog__actions`}>
                    <button
                        className={`${cn}__modal__cancelBtn`}
                        onClick={handleCloseDeleteConfirmModal}>
                        Cancel
                    </button>
                    <BorderBlueButtonV2 text='Delete' onClick={handleConfirmDelete} />
                </DialogActions>
            </Dialog>

            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                open={snackBarOpen}
                autoHideDuration={5000}
                onClose={handleSnackBarClose}>
                <Alert
                    onClose={handleSnackBarClose}
                    severity={snackBarType}
                    variant='filled'
                    sx={{ width: '100%' }}>
                    {snackBarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}
